import { useState, useEffect } from "react";

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/common/Card";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "./common/Dialog";
import * as utils from "../utils/utils";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/common/Select";
import { ArrowLeft } from "lucide-react";
import { useAuth } from "../lib/AuthContext";
import { supabase } from "../lib/supabase";
import {
  formatClockTimeRange,
  formatDateDMY,
  formatDateTimeDMY,
} from "../lib/ScheduleUtils";

import Alert from "../components/common/Alert";
import { Badge } from "../components/common/Badge";
import Button from "../components/common/Button";
import { Textarea } from "../components/common/Textarea";
import { Input } from "../components/common/Input";
import { Label } from "../components/common/Label";

import {
  Receipt,
  Search,
  Download,
  Eye,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  FileText,
  Calendar,
  User,
  DollarSign,
  Trash2,
  RotateCcw,
} from "lucide-react";

import customToast from "./common/Toaster";
import useBrandTranslation from "../hooks/useBrandTranslation";

export default function InvoiceManagement() {
  const { t } = useBrandTranslation();
  const { user } = useAuth();

  console.log("User in InvoiceManagement:", user?.id);
  const [loading, setLoading] = useState(false);
  const [appointments, setAppointments] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [afipConfig, setAfipConfig] = useState(null);
  const [isLoadingInfo, setIsLoadingInfo] = useState(true);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [invoiceData, setInvoiceData] = useState({
    invoiceType: "B",
    patientDocument: "",
    patientDocumentType: "DNI",
    conceptDescription: "",
    subtotal: "",
    ivaAmount: "0",
    totalAmount: "",
  });
  const initialFilters = {
    search: "",
    status: "all",
    dateFrom: "",
    dateTo: "",
  };

  const [filters, setFilters] = useState(initialFilters);

  const resetFilters = () => {
    setFilters(initialFilters);
  };
  const [activeTab, setActiveTab] = useState("pending");
  const [selectedInvoice, setSelectedInvoice] = useState(null);

  useEffect(() => {
    if (user?.id) {
      loadData();
    }
  }, [user]);

  const loadData = async () => {
    setIsLoadingInfo(true);
    await Promise.all([
      loadCompletedAppointments(),
      loadInvoices(),
      loadAfipConfig(),
    ]);
    setIsLoadingInfo(false);
  };
  console.log(appointments);
  const loadCompletedAppointments = async () => {
    try {
      const invoicedIds = await getInvoicedAppointmentIds();
      const discardedIds = await getDiscardedAppointmentIds();
      
      // Combinar ambos arrays de IDs y filtrar vacíos
      const allExcludedIds = [invoicedIds, discardedIds]
        .filter((ids) => ids && ids.trim().length > 0)
        .join(",");

      let query = supabase
        .from("appointments")
        .select(
          `
          *,
          patient:profiles!appointments_patient_id_fkey(name, email, phone)
        `
        )
        .eq("doctor_id", user.id)
        .eq("status", "completed")
        .order("date", { ascending: false });

      if (allExcludedIds && allExcludedIds.trim().length > 0) {
        query = query.not("id", "in", `(${allExcludedIds})`);
      }

      const { data, error } = await query;

      if (error) throw error;
      setAppointments(data || []);
    } catch (error) {
      console.error("Error loading appointments:", error);
    }
  };

  const getInvoicedAppointmentIds = async () => {
    const { data } = await supabase
      .from("invoices")
      .select("appointment_id")
      .eq("doctor_id", user.id);

    return data?.map((inv) => inv.appointment_id).join(",") || "";
  };

  const getDiscardedAppointmentIds = async () => {
    const { data } = await supabase
      .from("discarded_appointments")
      .select("appointment_id")
      .eq("doctor_id", user.id);

    return data?.map((disc) => disc.appointment_id).join(",") || "";
  };

  const loadInvoices = async () => {
    try {
      const { data, error } = await supabase
        .from("invoices")
        .select(
          `
          *,
          appointment:appointments(date, start_time, end_time)
          `
        )
        .eq("doctor_id", user.id)
        .order("created_at", { ascending: false });

      if (error) throw error;
      setInvoices(data || []);
    } catch (error) {
      console.error("Error loading invoices:", error);
    }
  };

  const loadAfipConfig = async () => {
    try {
      const { data, error } = await supabase
        .from("afip_configurations")
        .select("*")
        .eq("doctor_id", user.id)
        .single();

      if (error && error.code !== "PGRST116") throw error;
      setAfipConfig(data);
    } catch (error) {
      console.error("Error loading AFIP config:", error);
    }
  };

  const handleCreateInvoice = (appointment) => {
    setSelectedAppointment(appointment);
    setInvoiceData({
      invoiceType: "B",
      patientDocument: "",
      patientDocumentType: "DNI",
      conceptDescription: `Consulta médica - ${formatDateDMY(appointment.date)}`,
      subtotal: appointment.price?.toString() || "",
      ivaAmount: "0",
      totalAmount: appointment.price?.toString() || "",
    });
  };

  const handleViewInvoice = (invoice) => {
    setSelectedInvoice(invoice);
  };

  const handleDiscardAppointment = async (appointment) => {
    if (
      !confirm(t("app.billing.confirmDiscard", "¿Está seguro de que desea descartar este turno? No podrá facturarlo y dejará de aparecer en el listado."))
    ) {
      return;
    }

    setLoading(true);
    try {
      const { error } = await supabase.from("discarded_appointments").insert({
        appointment_id: appointment.id,
        doctor_id: user.id,
      });

      if (error) throw error;

      // Recargar los appointments para que desaparezca del listado
      await loadCompletedAppointments();
    } catch (error) {
      console.error("Error discarding appointment:", error);
      customToast(t("app.billing.discardError", "Error al descartar el turno"), "danger");
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = async (invoice) => {
    try {
      const response = await fetch(`/api/afip/generate-pdf/${invoice.id}`, {
        method: "GET",
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.style.display = "none";
        a.href = url;
        a.download = `Factura_${invoice.invoice_type}_${
          invoice.invoice_number || invoice.id
        }.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        customToast(t("app.billing.pdfError", "Error al generar el PDF"), "danger");
      }
    } catch (error) {
      console.error("Error downloading PDF:", error);
      customToast(t("app.billing.downloadError", "Error al descargar el PDF"), "danger");
    }
  };

  const calculateTotal = () => {
    const subtotal = Number.parseFloat(invoiceData.subtotal) || 0;
    const iva = Number.parseFloat(invoiceData.ivaAmount) || 0;
    return (subtotal + iva).toFixed(2);
  };

  const handleSubtotalChange = (value) => {
    const subtotal = Number.parseFloat(value) || 0;
    const ivaRate = invoiceData.invoiceType === "A" ? 0.21 : 0; // 21% IVA para tipo A
    const ivaAmount = (subtotal * ivaRate).toFixed(2);

    setInvoiceData((prev) => ({
      ...prev,
      subtotal: value,
      ivaAmount: ivaAmount,
      totalAmount: (subtotal + Number.parseFloat(ivaAmount)).toFixed(2),
    }));
  };

  const submitInvoice = async () => {
    if (!afipConfig) {
      customToast(t("app.billing.configureAfip", "Debe configurar AFIP antes de emitir facturas"), "warning");
      return;
    }

    setLoading(true);
    try {
      const response = await fetch("/api/afip/create-invoice", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          doctorId: user.id,
          appointmentId: selectedAppointment.id,
          invoiceData: {
            ...invoiceData,
            patientName:
              selectedAppointment.patient?.name ||
              selectedAppointment.patient_name,
            invoiceDate: new Date().toISOString().split("T")[0],
          },
        }),
      });

      const result = await response.json();

      if (result.success) {
        customToast(t("app.billing.invoiceCreated", "Factura creada exitosamente"), "success");
        setSelectedAppointment(null);
        loadData(); // Recargar datos
      } else {
        customToast(result.error || t("app.billing.invoiceError", "Error al crear la factura"), "danger");
      }
    } catch (error) {
      console.error("Error creating invoice:", error);
      customToast(t("app.billing.invoiceError", "Error al crear la factura"), "danger");
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: {
        color: "bg-yellow-100 text-yellow-800",
        icon: Clock,
        text: "Pendiente",
      },
      authorized: {
        color: "bg-green-100 text-green-800",
        icon: CheckCircle,
        text: "Autorizada",
      },
      rejected: {
        color: "bg-red-100 text-red-800",
        icon: XCircle,
        text: "Rechazada",
      },
      cancelled: {
        color: "bg-gray-100 text-gray-800",
        icon: XCircle,
        text: "Cancelada",
      },
    };

    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;

    return (
      <Badge className={config.color}>
        <Icon className="h-3 w-3 mr-1" />
        {config.text}
      </Badge>
    );
  };

  const filteredAppointments = appointments.filter((appointment) => {
    const matchesSearch =
      !filters.search ||
      appointment.patient?.name
        ?.toLowerCase()
        .includes(filters.search.toLowerCase()) ||
      appointment.patient_name
        ?.toLowerCase()
        .includes(filters.search.toLowerCase());

    const matchesDate =
      (!filters.dateFrom || appointment.date >= filters.dateFrom) &&
      (!filters.dateTo || appointment.date <= filters.dateTo);

    return matchesSearch && matchesDate;
  });

  const filteredInvoices = invoices.filter((invoice) => {
    const matchesSearch =
      !filters.search ||
      invoice.patient?.name
        ?.toLowerCase()
        .includes(filters.search.toLowerCase()) ||
      invoice.appointment?.patient?.name
        ?.toLowerCase()
        .includes(filters.search.toLowerCase()) ||
      invoice.invoice_number
        ?.toLowerCase()
        .includes(filters.search.toLowerCase());

    const matchesStatus =
      filters.status === "all" || invoice.status === filters.status;

    const matchesDate =
      (!filters.dateFrom || invoice.invoice_date >= filters.dateFrom) &&
      (!filters.dateTo || invoice.invoice_date <= filters.dateTo);

    return matchesSearch && matchesStatus && matchesDate;
  });

  if (!afipConfig && isLoadingInfo === false) {
    return (
      <div className="space-y-6 p-4">
        <Button
          variant="outline"
          onClick={() => utils.goBack()}
          className="flex items-center gap-2"
        >
          <ArrowLeft className="w-4 h-4" />
          Volver
        </Button>
        <div className="flex items-center gap-3">
          <Receipt className="h-8 w-8 text-blue-600" />
          <div>
            <h1 className="text-2xl font-bold">{t("app.billing.title", "Facturación")}</h1>
            <p className="text-gray-600">{t("app.billing.subtitle", "Gestione sus facturas electrónicas")}</p>
          </div>
        </div>

        <div className="flex bg-red-300 border border-red-500 p-2 gap-2 items-center justify-start rounded">
          <AlertTriangle className="h-4 w-4" />
          <div>
            Debe configurar AFIP antes de poder emitir facturas.{" "}
            <a href="/billing" className="text-blue-600 hover:underline">
              Ir a configuración
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4">
      <Button
        variant="outline"
        onClick={() => utils.goBack()}
        className="flex items-center gap-2"
      >
        <ArrowLeft className="w-4 h-4" />
        Volver
      </Button>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Receipt className="h-8 w-8 text-blue-600" />
          <div>
            <h1 className="text-2xl font-bold">{t("app.billing.title", "Facturación")}</h1>
            <p className="text-gray-600">{t("app.billing.subtitle", "Gestione sus facturas electrónicas")}</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Badge variant="outline" className="bg-green-300">
            <CheckCircle className="h-3 w-3 mr-1" />
            AFIP Configurado
          </Badge>
        </div>
      </div>
      {/* Filtros */}
      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div>
              <Label htmlFor="search">{t("app.commonUi.search", "Búsqueda")}</Label>
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <Input
                  id="search"
                  placeholder={t("app.billing.searchPlaceholder", "Paciente, número...")}
                  value={filters.search}
                  onChange={(e) =>
                    setFilters((prev) => ({ ...prev, search: e.target.value }))
                  }
                  className="pl-10"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="status">{t("app.billing.status", "Estado")}</Label>
              <div className="mt-2">
                <Select
                  value={filters.status}
                  onValueChange={(value) =>
                    setFilters((prev) => ({ ...prev, status: value }))
                  }
                >
                  <SelectTrigger className="!h-12">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t("app.billing.all", "Todos")}</SelectItem>
                    <SelectItem value="pending">{t("app.billing.pending", "Pendientes")}</SelectItem>
                    <SelectItem value="authorized">{t("app.billing.authorized", "Autorizadas")}</SelectItem>
                    <SelectItem value="rejected">{t("app.billing.rejected", "Rechazadas")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label htmlFor="dateFrom">{t("app.billing.from", "Desde")}</Label>
              <Input
                id="dateFrom"
                type="date"
                value={filters.dateFrom}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, dateFrom: e.target.value }))
                }
              />
            </div>

            <div>
              <Label htmlFor="dateTo">{t("app.billing.to", "Hasta")}</Label>
              <Input
                id="dateTo"
                type="date"
                value={filters.dateTo}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, dateTo: e.target.value }))
                }
              />
            </div>

            <div className="flex items-end">
              <Button
                variant="outline"
                onClick={resetFilters}
                className="w-full md:w-auto"
                disabled={
                  filters.search === "" &&
                  filters.status === "all" &&
                  filters.dateFrom === "" &&
                  filters.dateTo === ""
                }
              >
                <RotateCcw className="h-4 w-4 mr-2" />
                Resetear
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
      {/* Tabs */}
      <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg">
        <button
          onClick={() => setActiveTab("pending")}
          className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
            activeTab === "pending"
              ? "bg-white text-blue-600 shadow-sm"
              : "text-gray-600 hover:text-gray-900"
          }`}
        >
          Turnos por Facturar ({filteredAppointments.length})
        </button>
        <button
          onClick={() => setActiveTab("invoiced")}
          className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
            activeTab === "invoiced"
              ? "bg-white text-blue-600 shadow-sm"
              : "text-gray-600 hover:text-gray-900"
          }`}
        >
          Facturas Emitidas ({filteredInvoices.length})
        </button>
      </div>
      {/* Contenido de tabs */}
      {activeTab === "pending" && (
        <div className="grid gap-4 pb-[100px]">
          {filteredAppointments.length === 0 ? (
            <Card>
              <CardContent className="pt-6 text-center">
                <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">
                  No hay turnos completados pendientes de facturar
                </p>
              </CardContent>
            </Card>
          ) : (
            filteredAppointments.map((appointment) => (
              <Card key={appointment.id}>
                <CardContent className="pt-6">
                  <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex min-w-0 flex-1 items-start gap-3 sm:gap-4">
                      <div className="shrink-0 rounded-lg bg-blue-100 p-2">
                        <User className="h-5 w-5 text-blue-600" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 className="truncate font-semibold">
                          {appointment.patient?.name ||
                            appointment.patient_name}
                        </h3>
                        <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-600">
                          <span className="flex shrink-0 items-center gap-1">
                            <Calendar className="h-4 w-4 shrink-0" />
                            {formatDateDMY(appointment.date)}
                          </span>
                          <span className="shrink-0">
                            {formatClockTimeRange(appointment.start_time, appointment.end_time)}
                          </span>
                          {appointment.price && (
                            <span className="flex shrink-0 items-center gap-1">
                              <DollarSign className="h-4 w-4" />$
                              {appointment.price}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex shrink-0 flex-col gap-2 sm:flex-row">
                      <Button
                        variant="outline"
                        onClick={() => handleDiscardAppointment(appointment)}
                        disabled={loading}
                        className="w-full text-red-600 hover:bg-red-50 hover:text-red-700 sm:w-auto"
                      >
                        <Trash2 className="mr-1 h-4 w-4 shrink-0" />
                        Descartar
                      </Button>
                      <Button
                        onClick={() => handleCreateInvoice(appointment)}
                        className="w-full sm:w-auto"
                      >
                        Crear Factura
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      )}
      {activeTab === "invoiced" && (
        <div className="grid gap-4">
          {filteredInvoices.length === 0 ? (
            <Card>
              <CardContent className="pt-6 text-center">
                <Receipt className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">{t("app.billing.noInvoices", "No hay facturas emitidas")}</p>
              </CardContent>
            </Card>
          ) : (
            filteredInvoices.map((invoice) => (
              <Card key={invoice.id}>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="bg-green-100 p-2 rounded-lg">
                        <Receipt className="h-5 w-5 text-green-600" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold">
                            {invoice.patient?.name ||
                              invoice.appointment?.patient?.name}
                          </h3>
                          {getStatusBadge(invoice.status)}
                        </div>
                        <div className="flex items-center gap-4 text-sm text-gray-600">
                          <span>
                            Factura {invoice.invoice_type} -{" "}
                            {invoice.invoice_number || "Pendiente"}
                          </span>
                          <span>
                            {formatDateDMY(invoice.invoice_date)}
                          </span>
                          <span className="font-medium">
                            ${invoice.total_amount}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleViewInvoice(invoice)}
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        Ver
                      </Button>
                      {/* {invoice.status === "authorized" && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDownloadPDF(invoice)}
                        >
                          <Download className="h-4 w-4 mr-1" />
                          PDF
                        </Button>
                      )} */}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      )}
      {/* Dialog para crear factura */}
      <Dialog
        open={!!selectedAppointment}
        onOpenChange={() => setSelectedAppointment(null)}
      >
        <DialogContent className="flex h-[90dvh] max-h-[90dvh] w-[calc(100vw-1.5rem)] max-w-2xl flex-col overflow-hidden p-4 sm:p-8">
          {/* Header fijo */}
          <div className="shrink-0 space-y-1 pb-4">
            <DialogTitle className="text-xl font-bold sm:text-2xl">
              Crear Factura
            </DialogTitle>
            <p className="text-sm text-gray-600">
              Complete los datos para emitir la factura del turno
            </p>
          </div>

          {selectedAppointment && (
            <>
              {/* Contenido scrolleable */}
              <div className="min-h-0 flex-1 overflow-y-auto">
                <div className="space-y-6 pr-1">
                  {/* Datos del turno */}
                  <Card>
                    <CardHeader className="py-3 sm:py-6">
                      <CardTitle className="text-base sm:text-lg">
                        Datos del Turno
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2 sm:gap-4">
                        <div>
                          <span className="font-medium">{t("app.billing.patient", "Paciente:")}</span>
                          <p className="truncate">
                            {selectedAppointment.patient?.name ||
                              selectedAppointment.patient_name}
                          </p>
                        </div>
                        <div>
                          <span className="font-medium">{t("app.billing.date", "Fecha:")}</span>
                          <p>
                            {formatDateDMY(selectedAppointment.date)}
                          </p>
                        </div>
                        <div>
                          <span className="font-medium">{t("app.billing.time", "Horario:")}</span>
                          <p>
                            {formatClockTimeRange(
                              selectedAppointment.start_time,
                              selectedAppointment.end_time
                            )}
                          </p>
                        </div>
                        <div>
                          <span className="font-medium">{t("app.billing.type", "Tipo:")}</span>
                          <p>
                            {selectedAppointment.appointment_type === "private"
                              ? "Particular"
                              : "Obra Social"}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Datos de facturación */}
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div>
                      <Label htmlFor="invoiceType">{t("app.billing.invoiceType", "Tipo de Factura")}</Label>
                      <Select
                        value={invoiceData.invoiceType}
                        onValueChange={(value) =>
                          setInvoiceData((prev) => ({
                            ...prev,
                            invoiceType: value,
                          }))
                        }
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="A">
                            Factura A (Responsable Inscripto)
                          </SelectItem>
                          <SelectItem value="B">
                            Factura B (Consumidor Final)
                          </SelectItem>
                          <SelectItem value="C">{t("app.billing.invoiceC", "Factura C (Exento)")}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label htmlFor="patientDocumentType">{t("app.billing.docType", "Tipo Documento")}</Label>
                      <Select
                        value={invoiceData.patientDocumentType}
                        onValueChange={(value) =>
                          setInvoiceData((prev) => ({
                            ...prev,
                            patientDocumentType: value,
                          }))
                        }
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="DNI">DNI</SelectItem>
                          <SelectItem value="CUIT">CUIT</SelectItem>
                          <SelectItem value="CUIL">CUIL</SelectItem>
                          <SelectItem value="Pasaporte">{t("app.billing.passport", "Pasaporte")}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label htmlFor="patientDocument">{t("app.billing.docNumber", "Número Documento")}</Label>
                      <Input
                        id="patientDocument"
                        value={invoiceData.patientDocument}
                        onChange={(e) =>
                          setInvoiceData((prev) => ({
                            ...prev,
                            patientDocument: e.target.value,
                          }))
                        }
                        placeholder="12345678"
                      />
                    </div>

                    <div>
                      <Label htmlFor="subtotal">{t("app.billing.subtotal", "Subtotal")}</Label>
                      <Input
                        id="subtotal"
                        type="number"
                        step="0.01"
                        value={invoiceData.subtotal}
                        onChange={(e) =>
                          handleSubtotalChange(e.target.value)
                        }
                      />
                    </div>

                    <div>
                      <Label htmlFor="ivaAmount">IVA</Label>
                      <Input
                        id="ivaAmount"
                        type="number"
                        step="0.01"
                        value={invoiceData.ivaAmount}
                        onChange={(e) =>
                          setInvoiceData((prev) => ({
                            ...prev,
                            ivaAmount: e.target.value,
                          }))
                        }
                        disabled={invoiceData.invoiceType !== "A"}
                      />
                    </div>

                    <div>
                      <Label htmlFor="totalAmount">{t("app.billing.total", "Total")}</Label>
                      <Input
                        id="totalAmount"
                        type="number"
                        step="0.01"
                        value={calculateTotal()}
                        disabled
                        className="font-bold"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="conceptDescription">{t("app.billing.concept", "Concepto")}</Label>
                    <Textarea
                      id="conceptDescription"
                      value={invoiceData.conceptDescription}
                      onChange={(e) =>
                        setInvoiceData((prev) => ({
                          ...prev,
                          conceptDescription: e.target.value,
                        }))
                      }
                      rows={3}
                    />
                  </div>
                </div>
              </div>

              {/* Footer fijo con botones */}
              <div className="mt-4 flex shrink-0 flex-col gap-2 border-t border-gray-200 pt-4 sm:flex-row sm:justify-end sm:gap-4">
                <Button
                  variant="outline"
                  onClick={() => setSelectedAppointment(null)}
                  className="w-full sm:w-auto"
                >
                  Cancelar
                </Button>
                <Button
                  onClick={submitInvoice}
                  disabled={loading}
                  className="w-full sm:w-auto"
                >
                  {loading ? "Creando..." : "Crear Factura"}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
      {/* Dialog para ver factura */}
      <Dialog
        open={!!selectedInvoice}
        onOpenChange={() => setSelectedInvoice(null)}
      >
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <h1 className="text-2xl font-bold mb-2 flex flex-col gap-1">
            <DialogTitle>{t("app.billing.invoiceDetail", "Detalle de Factura")}</DialogTitle>
          </h1>
          <div className="mb-2">{t("app.billing.invoiceDetailHint", "Información completa de la factura emitida")}</div>

          {selectedInvoice && (
            <div className="space-y-6">
              {/* Header de la factura */}
              <div className="bg-gray-50 p-6 rounded-lg">
                <div className="flex justify-between items-start">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">
                      Factura {selectedInvoice.invoice_type}
                    </h2>
                    <p className="text-gray-600">
                      {selectedInvoice.invoice_number || "Número pendiente"}
                    </p>
                  </div>
                  <div className="text-right">
                    {getStatusBadge(selectedInvoice.status)}
                    <p className="text-sm text-gray-600 mt-1">
                      {formatDateDMY(selectedInvoice.invoice_date)}
                    </p>
                  </div>
                </div>
              </div>

              {/* Información del emisor y receptor */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">{t("app.billing.issuerData", "Datos del Emisor")}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2 text-sm">
                      <div>
                        <span className="font-medium">{t("app.billing.professional", "Profesional:")}</span>
                        <p>{user?.name || "Dr. " + user?.email}</p>
                      </div>
                      <div>
                        <span className="font-medium">CUIT:</span>
                        <p>{afipConfig?.cuit || t("app.billing.notConfigured", "No configurado")}</p>
                      </div>
                      <div>
                        <span className="font-medium">{t("app.billing.businessName", "Razón Social:")}</span>
                        <p>{afipConfig?.razon_social || t("app.billing.notConfigured", "No configurado")}</p>
                      </div>
                      <div>
                        <span className="font-medium">{t("app.billing.salesPoint", "Punto de Venta:")}</span>
                        <p>{afipConfig?.punto_venta || t("app.billing.notConfigured", "No configurado")}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">{t("app.billing.clientData", "Datos del Cliente")}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2 text-sm">
                      <div>
                        <span className="font-medium">{t("app.profile.name", "Nombre")}:</span>
                        <p>{selectedInvoice.patient_name}</p>
                      </div>
                      <div>
                        <span className="font-medium">{t("app.billing.document", "Documento:")}</span>
                        <p>
                          {selectedInvoice.patient_document_type}:{" "}
                          {selectedInvoice.patient_document}
                        </p>
                      </div>
                      <div>
                        <span className="font-medium">{t("app.billing.invoiceTypeLabel", "Tipo de Factura:")}</span>
                        <p>
                          {selectedInvoice.invoice_type === "A" &&
                            "Factura A (Responsable Inscripto)"}
                          {selectedInvoice.invoice_type === "B" &&
                            "Factura B (Consumidor Final)"}
                          {selectedInvoice.invoice_type === "C" &&
                            "Factura C (Exento)"}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Detalle del servicio */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">
                    Detalle del Servicio
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div>
                      <span className="font-medium">{t("app.billing.concept", "Concepto")}:</span>
                      <p className="mt-1">
                        {selectedInvoice.concept_description}
                      </p>
                    </div>

                    {selectedInvoice.appointment && (
                      <div className="bg-blue-50 p-4 rounded-lg">
                        <h4 className="font-medium text-blue-900 mb-2">
                          Información del Turno
                        </h4>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <span className="font-medium">{t("app.billing.date", "Fecha:")}</span>
                            <p>
                              {formatDateDMY(selectedInvoice.appointment.date)}
                            </p>
                          </div>
                          <div>
                            <span className="font-medium">{t("app.billing.time", "Horario:")}</span>
                            <p>
                              {formatClockTimeRange(
                                selectedInvoice.appointment.start_time,
                                selectedInvoice.appointment.end_time
                              )}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Totales */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">{t("app.billing.totals", "Totales")}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span>{t("app.billing.subtotal", "Subtotal")}:</span>
                      <span>${selectedInvoice.subtotal?.toFixed(2)}</span>
                    </div>
                    {selectedInvoice.iva_amount > 0 && (
                      <div className="flex justify-between">
                        <span>IVA (21%):</span>
                        <span>${selectedInvoice.iva_amount?.toFixed(2)}</span>
                      </div>
                    )}
                    <hr />
                    <div className="flex justify-between text-lg font-bold">
                      <span>{t("app.billing.total", "Total")}:</span>
                      <span>${selectedInvoice.total_amount?.toFixed(2)}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Información de AFIP */}
              {selectedInvoice.status === "authorized" && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">{t("app.billing.afipInfo", "Información AFIP")}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="font-medium">CAE:</span>
                        <p className="font-mono">{selectedInvoice.cae}</p>
                      </div>
                      <div>
                        <span className="font-medium">{t("app.billing.caeExpiry", "Vencimiento CAE:")}</span>
                        <p>
                          {selectedInvoice.cae_due_date
                            ? formatDateDMY(selectedInvoice.cae_due_date)
                            : "N/A"}
                        </p>
                      </div>
                      <div>
                        <span className="font-medium">
                          Fecha de Autorización:
                        </span>
                        <p>
                          {formatDateTimeDMY(selectedInvoice.created_at)}
                        </p>
                      </div>
                      <div>
                        <span className="font-medium">{t("app.billing.statusLabel", "Estado:")}</span>
                        <p className="text-green-600 font-medium">
                          Autorizada por AFIP
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Respuesta de error si la factura fue rechazada */}
              {selectedInvoice.status === "rejected" &&
                selectedInvoice.afip_response && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg text-red-600">
                        Error de AFIP
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="bg-red-50 p-4 rounded-lg">
                        <p className="text-red-800">
                          {JSON.parse(selectedInvoice.afip_response)?.error ||
                            "Error desconocido"}
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                )}

              {/* Botones de acción */}
              <div className="flex justify-end gap-4 pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={() => setSelectedInvoice(null)}
                >
                  Cerrar
                </Button>
                {selectedInvoice.status === "authorized" && (
                  <Button onClick={() => handleDownloadPDF(selectedInvoice)}>
                    <Download className="h-4 w-4 mr-2" />
                    Descargar PDF
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
